import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SeoArticleSchema from "@/components/SeoArticleSchema";
import { ContextualLinkPanel } from "@/components/ContextualLinkPanel";
import { InsightKeyTakeaway } from "@/components/InsightKeyTakeaway";

const shortlistBenefits = [
  "narrow the field to a manageable number of countries",
  "compare real tradeoffs instead of collecting endless information",
  "focus your deeper research on the options most worth exploring",
  "avoid getting pulled too far by hype, trends, or other people’s stories",
];

const decisionFactors = [
  "long-term affordability",
  "healthcare quality",
  "residency practicality",
  "infrastructure and daily convenience",
  "language comfort and ease of transition",
  "climate and day-to-day lifestyle",
  "safety and stability",
];

const tradeoffExamples = [
  "better healthcare, but higher cost",
  "easier daily life, but a harder residency path",
  "stronger infrastructure, but less lifestyle fit",
  "beautiful weather, but weaker affordability over time",
];

const stayOnShortlistQuestions = [
  "Can I realistically afford life there over time?",
  "Does the residency path seem workable for my situation?",
  "Would day-to-day life actually feel right for me?",
  "Do the healthcare, infrastructure, and practical basics feel strong enough?",
  "Is this country still appealing once I stop thinking like a tourist and start thinking like a resident?",
];

const dropOffReasons = [
  "the cost picture stops making sense",
  "the residency path looks too difficult or uncertain",
  "the language or lifestyle transition feels too heavy",
  "the country only works if everything goes perfectly",
  "it sounds exciting, but does not hold up against what matters most to you",
];

const focusedComparisonQuestions = [
  "Which of these countries best fits my actual budget?",
  "Which one feels more workable in real life?",
  "Which tradeoff matters more to me?",
  "What would still change the answer?",
];

const shortlistRules = [
  "remove countries that are no longer realistic",
  "keep the strongest options in play",
  "make your deeper research more focused and more useful",
];

const renderBulletList = (items, prefix, bulletClassName = "bg-amber-400/80") => (
  <ul className="mt-5 space-y-3 text-sm leading-7 text-stone-600" data-testid={`${prefix}-list`}>
    {items.map((item, index) => (
      <li key={item} className="flex gap-3" data-testid={`${prefix}-item-${index + 1}`}>
        <span className={`mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full ${bulletClassName}`} />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

export default function CreateCountryShortlistForMovingAbroad() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#f7f4ee] text-stone-900" data-testid="create-country-shortlist-page">
      <SeoArticleSchema
        headline="How to Create a Country Shortlist for Moving Abroad"
        description="Learn how to narrow your country options and build a realistic shortlist before moving abroad, based on your budget, priorities, and daily life needs."
        pathname="/insights/create-country-shortlist-for-moving-abroad"
        keywords={[
          "how to create a country shortlist",
          "moving abroad shortlist",
          "how to narrow country options abroad",
        ]}
      />
      <Header />

      <main className="px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <div className="mx-auto max-w-6xl space-y-8">
          <section className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-10 shadow-[0_18px_48px_rgba(28,25,23,0.06)] sm:px-10" data-testid="shortlist-hero-section">
            <div className="mb-5 h-1.5 w-16 rounded-full bg-amber-400/80" data-testid="shortlist-hero-accent" />
            <p className="rc-section-kicker mb-4" data-testid="shortlist-hero-kicker">Decision support</p>
            <h1 className="font-manrope text-4xl text-stone-900 sm:text-5xl" data-testid="shortlist-page-title">
              How to Create a Country Shortlist for Moving Abroad
            </h1>
            <div className="mt-6 max-w-4xl space-y-5 text-base leading-8 text-stone-600" data-testid="shortlist-page-intro">
              <p>
                Use your priorities, tradeoffs, and practical limits to narrow the field before your research starts to spiral.
              </p>
              <p data-testid="shortlist-page-role-note">
                If you are still trying to figure out what country fits you overall, start with{" "}
                <Link
                  to="/insights/what-country-should-i-move-to"
                  className="font-medium text-stone-700 underline-offset-4 transition-colors duration-200 hover:text-stone-900 hover:underline"
                  data-testid="shortlist-page-what-country-link"
                >
                  what actually drives country fit
                </Link>
                .
              </p>
            </div>
          </section>

          <InsightKeyTakeaway
            testIdPrefix="shortlist-key-takeaway"
            paragraphs={[
              "A useful country shortlist narrows broad interest into a smaller set of countries that still look realistic once your priorities, budget, healthcare expectations, and residency practicality are doing the sorting.",
              "The goal is not to keep every appealing country alive. The goal is to remove weaker fits early so your deeper research becomes more focused, more honest, and more useful.",
            ]}
          />

          <section className="grid gap-6 lg:grid-cols-[1.05fr_1.2fr]" data-testid="shortlist-context-grid">
            <article className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-8" data-testid="shortlist-why-it-matters-section">
              <h2 className="font-manrope text-2xl text-stone-900" data-testid="shortlist-why-it-matters-title">Why does a shortlist matter?</h2>
              <div className="mt-5 space-y-4 text-base leading-8 text-stone-600" data-testid="shortlist-why-it-matters-copy">
                <p>
                  A lot of people start with a long list of countries that sound appealing. That is normal, but it can quickly turn into scattered research, conflicting opinions, and too many tabs open without a clearer decision.
                </p>
                <p>
                  A good shortlist helps you focus on the countries that are most likely to fit your life, budget, and move goals.
                </p>
              </div>
            </article>

            <article className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-8" data-testid="shortlist-good-list-section">
              <h2 className="font-manrope text-2xl text-stone-900" data-testid="shortlist-good-list-title">What should a good shortlist actually do?</h2>
              {renderBulletList(shortlistBenefits, "shortlist-benefits")}
              <p className="mt-5 text-base leading-8 text-stone-600" data-testid="shortlist-good-list-summary">
                The goal is not to find the perfect country immediately. It is to identify the few countries most worth comparing seriously.
              </p>
            </article>
          </section>

          <section className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-10" data-testid="shortlist-what-matters-section">
            <h2 className="font-manrope text-2xl text-stone-900" data-testid="shortlist-what-matters-title">What should carry the most weight in the shortlist?</h2>
            <div className="mt-5 max-w-4xl space-y-4 text-base leading-8 text-stone-600">
              <p data-testid="shortlist-what-matters-copy-1">
                Before you create a shortlist, it helps to get clear on the factors that should carry the most weight in your decision.
              </p>
              <p data-testid="shortlist-what-matters-copy-2">
                That usually includes things like:
              </p>
            </div>
            <div className="mt-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="shortlist-factors-grid">
              {decisionFactors.map((item, index) => (
                <div key={item} className="rounded-[1.35rem] border border-stone-200/80 bg-stone-50/75 px-4 py-3" data-testid={`shortlist-factor-${index + 1}`}>
                  <p className="text-sm leading-7 text-stone-700">{item}</p>
                </div>
              ))}
            </div>
            <p className="mt-6 text-base leading-8 text-stone-600" data-testid="shortlist-what-matters-copy-3">
              This is where many people get stuck. They start researching countries before deciding what they actually need from one.
            </p>
          </section>

          <section className="grid gap-6 lg:grid-cols-2" data-testid="shortlist-tradeoff-grid">
            <article className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-8" data-testid="shortlist-tradeoffs-section">
              <h2 className="font-manrope text-2xl text-stone-900" data-testid="shortlist-tradeoffs-title">How should tradeoffs shape the shortlist?</h2>
              <div className="mt-5 space-y-4 text-base leading-8 text-stone-600">
                <p data-testid="shortlist-tradeoffs-copy-1">
                  Most relocation decisions come down to tradeoffs.
                </p>
                <p data-testid="shortlist-tradeoffs-copy-2">
                  A country may offer:
                </p>
              </div>
              {renderBulletList(tradeoffExamples, "shortlist-tradeoff-examples")}
              <p className="mt-5 text-base leading-8 text-stone-600" data-testid="shortlist-tradeoffs-copy-3">
                A useful shortlist should reflect those tradeoffs honestly.
              </p>
              <p className="mt-4 text-base leading-8 text-stone-800" data-testid="shortlist-tradeoffs-copy-4">
                If every country on your shortlist sounds equally good, the shortlist probably is not doing enough work yet.
              </p>
            </article>

            <article className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-8" data-testid="shortlist-keep-small-section">
              <h2 className="font-manrope text-2xl text-stone-900" data-testid="shortlist-keep-small-title">How many countries should stay on the list?</h2>
              <div className="mt-5 space-y-4 text-base leading-8 text-stone-600">
                <p data-testid="shortlist-keep-small-copy-1">
                  For most people, a shortlist should usually be around:
                </p>
              </div>
              <div className="mt-5 rounded-[1.6rem] border border-amber-200/80 bg-amber-50/70 p-5" data-testid="shortlist-size-callout">
                <p className="text-sm font-semibold uppercase tracking-[0.16em] text-stone-500">Recommended range</p>
                <p className="mt-3 font-manrope text-3xl text-stone-900" data-testid="shortlist-size-range">2 to 4 countries</p>
                <p className="mt-3 text-sm leading-7 text-stone-600">
                  for serious comparison
                </p>
              </div>
              <p className="mt-5 text-base leading-8 text-stone-600" data-testid="shortlist-keep-small-copy-2">
                Once the list gets too long, it becomes much harder to compare clearly.
              </p>
              <p className="mt-4 text-base leading-8 text-stone-800" data-testid="shortlist-keep-small-copy-3">
                If you are still looking at 8 or 10 countries, you are probably still in discovery mode, not decision mode.
              </p>
            </article>
          </section>

          <section className="grid gap-6 lg:grid-cols-2" data-testid="shortlist-stay-drop-grid">
            <article className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-8" data-testid="shortlist-stay-section">
              <h2 className="font-manrope text-2xl text-stone-900" data-testid="shortlist-stay-title">What should make a country stay on the shortlist?</h2>
              <p className="mt-5 text-base leading-8 text-stone-600" data-testid="shortlist-stay-copy">
                A country should stay on your shortlist if it still holds up when you ask questions like:
              </p>
              {renderBulletList(stayOnShortlistQuestions, "shortlist-stay-questions")}
            </article>

            <article className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-8" data-testid="shortlist-drop-section">
              <h2 className="font-manrope text-2xl text-stone-900" data-testid="shortlist-drop-title">What should make a country drop off the shortlist?</h2>
              <p className="mt-5 text-base leading-8 text-stone-600" data-testid="shortlist-drop-copy-1">
                A country may need to drop off your shortlist if:
              </p>
              {renderBulletList(dropOffReasons, "shortlist-drop-reasons", "bg-stone-400")}
              <p className="mt-5 text-base leading-8 text-stone-800" data-testid="shortlist-drop-copy-2">
                Dropping countries is not failure. It is progress.
              </p>
            </article>
          </section>

          <section className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-10" data-testid="shortlist-how-to-use-section">
            <h2 className="font-manrope text-2xl text-stone-900" data-testid="shortlist-how-to-use-title">How should you use the shortlist once you have it?</h2>
            <div className="mt-5 max-w-4xl space-y-4 text-base leading-8 text-stone-600">
              <p data-testid="shortlist-how-to-use-copy-1">
                Once you have a strong shortlist, the next step is not more random browsing. It is more focused comparison.
              </p>
              <p data-testid="shortlist-how-to-use-copy-2">
                That usually means going deeper on questions like:
              </p>
            </div>
            <div className="mt-6 grid gap-4 md:grid-cols-2" data-testid="shortlist-focus-questions-grid">
              {focusedComparisonQuestions.map((item, index) => (
                <div key={item} className="rounded-[1.35rem] border border-stone-200/80 bg-stone-50/75 p-4" data-testid={`shortlist-focus-question-${index + 1}`}>
                  <p className="text-sm leading-7 text-stone-700">{item}</p>
                </div>
              ))}
            </div>
            <p className="mt-6 text-base leading-8 text-stone-600" data-testid="shortlist-how-to-use-copy-3">
              This is where the shortlist becomes useful. It helps you stop researching in circles and start comparing the right options.
            </p>
            <p className="mt-4 text-base leading-8 text-stone-600" data-testid="shortlist-how-to-use-copy-4">
              Once you have narrowed your options, a side-by-side example like{" "}
              <Link
                to="/insights/portugal-vs-spain-living-abroad"
                className="font-medium text-stone-700 underline-offset-4 transition-colors duration-200 hover:text-stone-900 hover:underline"
                data-testid="shortlist-portugal-spain-link"
              >
                  Portugal vs Spain: Which Country Fits Your Priorities?
              </Link>
              {" "}helps clarify which one fits better.
            </p>
          </section>

          <section className="grid gap-6 lg:grid-cols-[1.05fr_1.2fr]" data-testid="shortlist-framework-grid">
            <article className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-8" data-testid="shortlist-simple-way-section">
              <h2 className="font-manrope text-2xl text-stone-900" data-testid="shortlist-simple-way-title">What is the simplest way to judge whether the shortlist is working?</h2>
              <p className="mt-5 text-base leading-8 text-stone-600" data-testid="shortlist-simple-way-copy-1">
                A good shortlist should do three things:
              </p>
              {renderBulletList(shortlistRules, "shortlist-rules")}
              <p className="mt-5 text-base leading-8 text-stone-800" data-testid="shortlist-simple-way-copy-2">
                If it is not doing those three things, it is probably still too broad.
              </p>
            </article>

            <article className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-8" data-testid="shortlist-rc-fit-section">
              <div className="mb-5 h-1.5 w-14 rounded-full bg-amber-400/80" data-testid="shortlist-rc-fit-accent" />
              <h2 className="font-manrope text-2xl text-stone-900" data-testid="shortlist-rc-fit-title">Where does Relocation Clarity fit into this stage?</h2>
              <div className="mt-5 space-y-4 text-base leading-8 text-stone-600">
                <p data-testid="shortlist-rc-fit-copy-1">
                  Relocation Clarity is designed to help with this exact stage of the process.
                </p>
                <p data-testid="shortlist-rc-fit-copy-2">
                  It helps you define what matters most, narrow the shortlist, and focus your deeper research on the countries that still fit your life.
                </p>
                <p data-testid="shortlist-rc-fit-copy-3">
                  That way, you can spend less time researching countries that are only loosely appealing and more time comparing the ones that are actually worth serious consideration.
                </p>
                <p data-testid="shortlist-rc-fit-copy-4">
                  You can also{" "}
                  <Link
                    to="/assessment"
                    className="font-medium text-stone-700 underline-offset-4 transition-colors duration-200 hover:text-stone-900 hover:underline"
                    data-testid="shortlist-assessment-link"
                  >
                    start your assessment
                  </Link>
                  {" "}to see which countries best match your priorities.
                </p>
              </div>
            </article>
          </section>

          <ContextualLinkPanel
            kicker="Once the shortlist is tighter"
            title="These are the most natural next pages after this stage."
            description="A shortlist becomes more useful when you test it against a real comparison, see what the paid decision support looks like, and then personalize the process if you are ready."
            testIdPrefix="shortlist-next-steps"
            items={[
              {
                slug: "comparison",
                eyebrow: "Test the shortlist",
                title: "See a country comparison in action",
                description: "Compare Portugal and Spain to see how shortlist tradeoffs become easier to weigh side by side.",
                ctaLabel: "Open the comparison",
                to: "/insights/portugal-vs-spain-living-abroad",
              },
              {
                slug: "sample-blueprint",
                eyebrow: "See the paid layer",
                title: "Preview a Sample Blueprint",
                description: "See how a shortlist turns into a fuller recommendation with tradeoffs, residency context, and next steps.",
                ctaLabel: "Preview the Blueprint",
                to: "/sample-plan",
              },
              {
                slug: "assessment",
                eyebrow: "Personalize it",
                title: "Start your assessment",
                description: "Use your own priorities, budget, and relocation goals to build the shortlist around your situation.",
                ctaLabel: "Begin the assessment",
                to: "/assessment",
              },
            ]}
          />

          <section className="rounded-[2rem] border border-stone-600/70 bg-stone-900 px-7 py-9 text-stone-50 shadow-[0_18px_48px_rgba(28,25,23,0.12)] sm:px-10" data-testid="shortlist-cta-section">
            <div className="mb-5 h-1.5 w-14 rounded-full bg-amber-400/85" data-testid="shortlist-cta-accent" />
            <p className="text-sm font-medium uppercase tracking-[0.18em] text-stone-300" data-testid="shortlist-cta-kicker">Next step</p>
            <h2 className="mt-3 font-manrope text-3xl text-stone-50" data-testid="shortlist-cta-title">Ready to narrow the shortlist with more structure?</h2>
            <p className="mt-4 max-w-3xl text-base leading-8 text-stone-300" data-testid="shortlist-cta-copy">
              The assessment helps you weigh what matters most, keep realistic countries in view, and separate broad interest from the options worth serious comparison.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-4">
              <Button
                className="rounded-full bg-white px-6 text-stone-900 hover:bg-amber-100"
                data-testid="shortlist-cta-start-assessment-button"
                onClick={() => navigate("/assessment")}
              >
                Start your assessment
              </Button>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}